import { createContext, useContext, useState, useEffect } from "react";
import {
  cacheProtocol,
  getCachedProtocol,
  removeCachedProtocol,
  clearAllProtocols,
} from "../utils/protocolsCache";
import { logRemoteError } from "../utils/remoteLogger";

const STORAGE_KEY = "offlineRequests";

const OfflineQueueContext = createContext();

export const useOfflineQueue = () => useContext(OfflineQueueContext);

export const OfflineQueueProvider = ({ children }) => {
  const [queue, setQueue] = useState(() =>
    JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")
  );
  const [protocols, setProtocols] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [syncStatus, setSyncStatus] = useState(null);

  const refreshProtocols = async () => {
    const updatedQueue = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    setQueue(updatedQueue);

    const entries = [];
    for (const { id } of updatedQueue) {
      const data = await getCachedProtocol(id);
      if (data) entries.push({ id, data });
    }
    setProtocols(entries);
  };

  useEffect(() => {
    refreshProtocols();
  }, []);

  useEffect(() => {
    window.addEventListener("online", retryQueue);
    return () => window.removeEventListener("online", retryQueue);
  }, []);

  const queueRequest = async (url, data, id) => {
    const existing = queue.filter((req) => !(req.url === url && req.id === id));
    await cacheProtocol(id, data);
    const newQueue = [...existing, { url, id, attempts: 0, lastTried: 0 }];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newQueue));
    setQueue(newQueue);
    await refreshProtocols();
  };

  const retryQueue = async () => {
    const token = (() => {
      const name = "token=";
      const cookies = decodeURIComponent(document.cookie).split(";");
      for (let c of cookies) {
        c = c.trim();
        if (c.startsWith(name)) return c.substring(name.length);
      }
      return undefined;
    })();

    const current = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    const total = current.length;
    let processed = 0;
    const remaining = [];

    setIsSyncing(true);
    setProgress({ current: 0, total });

    for (const item of current) {
      const { url, id, attempts, lastTried } = item;
      const data = await getCachedProtocol(id);
      if (!data) continue;

      processed++;
      setProgress({ current: processed, total });

     // console.log(data);

      const form = new FormData();

      for (const key in data) {
        if (key === "attachments") {
          Object.entries(data.attachments).forEach(([key, file]) => {
            if (file) {
              form.append(key, file);
            }
          });
        } else if (key === "protocol_files") {
          Object.entries(data.protocol_files).forEach(([key, file]) => {
            if (file) {
              form.append(key, file);
            }
          });
          form.append("protocol_files", JSON.stringify(Object.keys(data.protocol_files)));
        } else {
          form.append(
            key,
            typeof data[key] === "object"
              ? JSON.stringify(data[key])
              : data[key]
          );
        }
      }

      // Streszczenie requestu do logów (bez samych plików/blobów).
      const requestSummary = {
        id,
        attempts,
        fields: Object.keys(data),
        attachments:
          data.attachments && typeof data.attachments === "object"
            ? Object.keys(data.attachments)
            : [],
        protocol_files:
          data.protocol_files && typeof data.protocol_files === "object"
            ? Object.keys(data.protocol_files)
            : [],
      };

      let res = null;
      try {
        res = await fetch(url, {
          method: "POST",
          headers: {
            // no Content-Type here, let the browser set it
            ...(token ? { "x-auth-token": token } : {}),
          },
          body: form,
        });
      } catch (networkErr) {
        // Sieć/serwer nieosiągalny - fetch odrzucony (status "0").
        logRemoteError({
          procedure: "protocolSync/retryQueue",
          method: "POST",
          url,
          status: 0,
          message: networkErr?.message || "fetch failed",
          request: requestSummary,
          extra: { offline: true },
        });
      }

      if (res && res.ok) {
        try {
          await removeCachedProtocol(id);
        } catch (cacheErr) {
          // Protokół wysłany, ale nie udało się go usunąć z cache -> zostanie
          // ponowiony przy następnej synchronizacji. Zaloguj, ale nie przerywaj.
          logRemoteError({
            procedure: "protocolSync/removeCachedProtocol",
            url,
            status: res.status,
            message: cacheErr?.message || "removeCachedProtocol failed",
            request: requestSummary,
          });
        }
      } else {
        // Odpowiedź serwera z błędem (4xx/5xx) - loguj status i treść odpowiedzi.
        if (res && !res.ok) {
          const bodyText = await res.text().catch(() => "");
          logRemoteError({
            procedure: "protocolSync/retryQueue",
            method: "POST",
            url,
            status: res.status,
            message: `HTTP ${res.status} ${res.statusText || ""}`.trim(),
            response: bodyText,
            request: requestSummary,
          });
        }

        remaining.push({
          ...item,
          attempts: attempts + 1,
          lastTried: Date.now(),
        });
      }
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(remaining));
    setQueue(remaining);
    await refreshProtocols();

    setIsSyncing(false);
    const successCount = total - remaining.length;
    const failedCount = remaining.length;

    setSyncStatus(
      successCount > 0 && failedCount === 0
        ? "success"
        : failedCount > 0
        ? "error"
        : null
    );
    setTimeout(() => setSyncStatus(null), 4000);
  };

  const removeFromQueue = async (id) => {
    const updatedQueue = queue.filter((item) => item.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedQueue));
    await removeCachedProtocol(id);
    setQueue(updatedQueue);
    await refreshProtocols();
  };

  const clearQueue = async () => {
    localStorage.removeItem(STORAGE_KEY);
    await clearAllProtocols();
    setQueue([]);
    setProtocols([]);
  };

  return (
    <OfflineQueueContext.Provider
      value={{
        queueLength: queue.length,
        protocols,
        isSyncing,
        progress,
        syncStatus,
        queueRequest,
        retryQueue,
        clearQueue,
        refreshProtocols,
        removeFromQueue,
      }}
    >
      {children}
    </OfflineQueueContext.Provider>
  );
};
